/**
 * The lookup-enum label suffix a grid cell shows next to its raw key:
 * `3 · Active`.
 *
 * Two-grid parity (CLAUDE.md § "Two Results Grids"): both the Query Editor
 * cell renderer (`useCellRenderer.tsx`) and the Table Explorer row
 * (`TableExplorerGridRow.tsx`) render their FK cell through
 * {@link withLookupLabel}, so the label can't drift between them.
 *
 * The label is **presentation only**. It is appended AFTER the existing FK
 * link, never inside it and never in place of the value, so copy, export,
 * sort, filter and FK navigation keep seeing exactly the stored key (the same
 * invariant as PP-01 numeric grouping and the spatial copy transform).
 *
 * Rendering rules — all three cases below look exactly like they did before
 * lookup enums existed:
 *  - key not in the cached map (`!labels.has(key)`) — unknown key, no suffix
 *    (this is also Wave 3.4's staleness signal);
 *  - key present with a `null` label — the target row's label column was
 *    genuinely NULL, so there is nothing to show;
 *  - no declaration / no cache for the column — `info` is undefined.
 */

import type { ReactNode } from 'react';
import type { LookupLabelInfo } from '../../hooks/useLookupLabelMaps';
import { formatRelativeTime } from '../../utils/formatRelativeTime';
import { qualifyLookupTarget } from '../../utils/lookupEnumTarget';

// `inline-flex`, not `flex`: an FK column is usually numeric, and the grid
// right-aligns numeric `<td>`s (PP-01). A block-level flex wrapper would
// ignore that and pull only the wrapped cells back to the left.
const WRAPPER_CLASS = 'inline-flex items-center max-w-full min-w-0 align-middle';

function LookupLabelSuffix({ info, label }: { info: LookupLabelInfo; label: string }) {
  const target = info.target;
  const title = target
    ? `${label} — from ${qualifyLookupTarget(target.toSchema, target.toTable)}${
        info.sampledAt ? ` (cached ${formatRelativeTime(info.sampledAt)})` : ''
      }`
    : label;
  return (
    <span
      className="ml-1 shrink-0 text-muted-foreground truncate"
      data-testid="grid-cell-lookup-label"
      title={title}
    >
      · {label}
    </span>
  );
}

/**
 * Append the lookup label to an already-rendered FK cell. Returns `content`
 * UNCHANGED (no extra wrapper, no DOM change at all) whenever the cell has no
 * label to show, so non-lookup cells keep their exact previous markup.
 */
export function withLookupLabel(
  content: ReactNode,
  info: LookupLabelInfo | undefined,
  value: unknown,
  options?: { stableWrapper?: boolean },
): ReactNode {
  // `stableWrapper` keeps the wrapper element present even when there is no
  // label yet, so `content`'s position in the React tree never changes.
  //
  // It matters only where `content` OWNS STATE. The label map arrives
  // asynchronously and an `explicit` lookup column emits no entry at all until
  // its cache lands (`useLookupLabelMaps`), so without this the cell flips from
  // bare `content` to `<span>{content}</span>` the moment a label resolves —
  // a different element at that position, which unmounts `content` and
  // discards its state. For an FK cell that means an open popover vanishing
  // while the user is reading it. Plain-value cells have nothing to lose and
  // keep their exact previous markup (no wrapper at all).
  const stable = options?.stableWrapper ?? false;
  if (!info) {
    return stable ? <span className={WRAPPER_CLASS}>{content}</span> : content;
  }
  // The cache keys its pairs by the STRINGIFIED value, so the cell value is
  // stringified the same way. `null` is a present-but-empty label; `undefined`
  // is an unknown key. Neither renders a suffix.
  const label = info.labels.get(String(value));
  if (label == null) {
    return stable ? <span className={WRAPPER_CLASS}>{content}</span> : content;
  }
  // `inline-flex`, not `flex`: an FK column is usually numeric, and the grid
  // right-aligns numeric `<td>`s (PP-01). A block-level flex wrapper would
  // ignore that and pull only the LABELLED cells back to the left.
  return (
    <span className={WRAPPER_CLASS}>
      {content}
      <LookupLabelSuffix info={info} label={label} />
    </span>
  );
}
